import { isNil } from 'lodash';
import React, { useContext } from 'react';
import { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { View } from '@shared/components/View';
import { MBTextBody1 } from '@shared/components/typography';
import { DateRangeField } from '@shared/data/entity/fields/DateRangeField';
import { ColorThemeContext } from '@shared/styles';
import { getPartialFormattedDateTimeRangeParts } from '@shared/util/date';
import { i18n } from '@shared/util/i18n';
import { CardCollectionViewFieldContainer } from './CardCollectionViewFieldContainer';

interface Props {
  field: DateRangeField;
  style: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
}

export function DateRangeCollectionViewField({ field, style, textStyle }: Props) {
  if (field.isEmpty) {
    return null;
  }

  return (
    <CardCollectionViewFieldContainer style={style}>
      <FormattedDateRange field={field} textStyle={textStyle} />
    </CardCollectionViewFieldContainer>
  );
}

export function FormattedDateRange({
  field,
  textStyle,
}: {
  field: DateRangeField;
  textStyle?: StyleProp<TextStyle>;
}) {
  const { start, end } = field.dateRange;
  const { from, to } = getPartialFormattedDateTimeRangeParts(start, end);
  return (
    <View row wrap gap={0.75}>
      <FormattedDateRangePart
        text={from}
        placeholder={i18n('workflowTemplate.component.dateRange.startPlaceholder')}
        textStyle={textStyle}
      />
      <MBTextBody1 style={textStyle}>–</MBTextBody1>
      <FormattedDateRangePart
        text={to}
        placeholder={i18n('workflowTemplate.component.dateRange.endPlaceholder')}
        textStyle={textStyle}
      />
    </View>
  );
}

function FormattedDateRangePart({
  text,
  placeholder,
  textStyle,
}: {
  text?: string;
  placeholder: string;
  textStyle: StyleProp<TextStyle>;
}) {
  const colors = useContext(ColorThemeContext);
  const isPlaceholder = isNil(text);
  return (
    <MBTextBody1 style={[textStyle, isPlaceholder && { color: colors.foregroundInactive }]}>
      {text ?? placeholder}
    </MBTextBody1>
  );
}
